import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useGoogleOneTapLogin } from '@react-oauth/google';
import { lazy, memo, Suspense, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Loading from '../../components/layouts/Loading';
import { AuthContext } from '../../contexts/AuthContext';

const AlertMessage = lazy(() =>
  import('../../components/layouts/AlertMessage')
);
const Copyright = lazy(() => import('../../components/layouts/Copyright'));

const LoginForm = () => {
  const { loginUser } = useContext(AuthContext);
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: '',
  });
  const [alert, setAlert] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [captcha, setCaptcha] = useState(true);
  const { username, password } = loginForm;
  const { t } = useTranslation();

  const onChangeLoginForm = useCallback(
    (event) => {
      setLoginForm({ ...loginForm, [event.target.name]: event.target.value });
    },
    [loginForm]
  );

  const handleClickShowPassword = useCallback(() => {
    setShowPassword(!showPassword);
  }, [showPassword]);

  const handleMouseDownPassword = useCallback((event) => {
    event.preventDefault();
  }, []);

  const onSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!username) {
        setAlert({
          type: 'error',
          message: 'username is required',
        });
        setTimeout(() => setAlert(null), 30000);
        return;
      }
      if (!password) {
        setAlert({
          type: 'error',
          message: 'password is required',
        });
        setTimeout(() => setAlert(null), 30000);
        return;
      }
      if (process.env.NODE_ENV !== 'production') {
        try {
          const loginData = await loginUser(loginForm);
          if (!loginData.success) {
            setAlert({ type: 'error', message: loginData.message });
            setTimeout(() => setAlert(null), 30000);
          }
        } catch (error) {
          console.log(error);
        }
      } else {
        if (captcha) {
          try {
            const loginData = await loginUser(loginForm);
            if (!loginData.success) {
              setAlert({ type: 'error', message: loginData.message });
              setTimeout(() => setAlert(null), 30000);
            }
          } catch (error) {
            console.log(error);
          }
        } else {
          setAlert({ type: 'error', message: t('captcha') });
          setTimeout(() => setAlert(null), 30000);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [captcha, loginForm, loginUser]
  );

  const onChange = useCallback((value) => {
    if (value) {
      setCaptcha(true);
    } else {
      setCaptcha(false);
    }
  }, []);

  useGoogleOneTapLogin({
    onSuccess: async (credentialResponse) => {
      const loginData = await loginUser(loginForm, credentialResponse);
      if (!loginData.success) {
        setAlert({ type: 'error', message: loginData.message });
        setTimeout(() => setAlert(null), 30000);
      }
    },
    onError: (error) => console.log(error),
  });

  return (
    <>
      <Typography component="h1" variant="h5">
        {t(`login.title`)}
      </Typography>
      <Box component="form" onSubmit={onSubmit} sx={{ mt: 1 }}>
        <Suspense fallback={<Loading />}>
          <AlertMessage info={alert} />
        </Suspense>
        <TextField
          margin="normal"
          fullWidth
          label={t(`login.username`)}
          name="username"
          autoComplete="username"
          autoFocus
          onChange={onChangeLoginForm}
          value={username}
        />
        <TextField
          margin="normal"
          fullWidth
          name="password"
          label={t(`login.password`)}
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          onChange={onChangeLoginForm}
          value={password}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {/* <ReCAPTCHA
          style={{ textAlign: '-webkit-center' }}
          sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY}
          onChange={onChange}
        /> */}
        <Button type="submit" fullWidth variant="contained" sx={{ my: 2 }}>
          {t(`login.btn`)}
        </Button>
        <Grid container>
          <Grid item xs>
            <Link to="/reset-password" variant="body2">
              {t(`login.forgotPass`)}
            </Link>
          </Grid>
          <Grid item>
            <Link to="/register" variant="body2">
              {t(`login.signUp`)}
            </Link>
          </Grid>
        </Grid>
        <Suspense fallback={<Loading />}>
          <Copyright sx={{ mt: 5 }} />
        </Suspense>
      </Box>
    </>
  );
};

export default memo(LoginForm);
